export type ReturnLabelErrorCategory =
  | 'CONNECTION'
  | 'ADDRESS'
  | 'PARCEL'
  | 'METHOD'
  | 'ACCOUNT_ACTION'
  | 'TRANSIENT'
  | 'UNKNOWN'
  | 'INTERNAL';

export type ReturnLabelFieldError = {
  owner: string;
  field: string;
  code: string;
  message: string;
};

type ReturnLabelErrorContract = {
  code?: unknown;
  category?: unknown;
  action?: unknown;
  outcome?: unknown;
  retryDisposition?: unknown;
  retryAfterSeconds?: unknown;
  reconnectRequired?: unknown;
  fieldErrors?: unknown;
  supportReference?: unknown;
};

type GraphQLErrorLike = {
  message?: string;
  extensions?: Record<string, unknown> | null;
};

type ApolloErrorLike = {
  message?: string;
  graphQLErrors?: readonly (GraphQLErrorLike | null | undefined)[];
  networkError?: unknown;
};

export type ReturnLabelErrorPresentation = {
  kind: ReturnLabelErrorCategory;
  code: string | null;
  category: string | null;
  action: string | null;
  outcome: string;
  retryDisposition: string | null;
  retryAfterSeconds: number | null;
  reconnectRequired: boolean;
  fieldErrors: ReturnLabelFieldError[];
  supportReference: string | null;
  message: string;
  actionLabel?: string;
  actionTarget?: string;
  canRetry: boolean;
  shouldPoll: boolean;
  mayHaveCreatedLabel: boolean;
  persistedStatus?: string;
  lockRetained?: boolean;
};

const RETURN_SETTINGS_PATH = '/settings/resolution-portal/returns/carriers';
const SUPPORT_EMAIL_PATH = 'mailto:support@shipaid.com';

const stringValue = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value.trim() : null;

const upperValue = (value: unknown): string | null =>
  stringValue(value)?.toUpperCase() ?? null;

const finiteNonNegativeNumber = (value: unknown): number | null => {
  if (
    (typeof value !== 'number' && typeof value !== 'string') ||
    (typeof value === 'string' && !value.trim())
  ) {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0
    ? Math.min(parsed, 86_400)
    : null;
};

const FIELD_PRESENTATIONS: Record<
  string,
  { label: string; maxLength?: number }
> = {
  line_1: { label: 'Street address', maxLength: 35 },
  line_2: { label: 'Address line 2', maxLength: 35 },
  company_name: { label: 'Company or location name', maxLength: 27 },
  contact_name: { label: 'Contact name', maxLength: 22 },
  contact_phone: { label: 'Phone number', maxLength: 20 },
  contact_email: { label: 'Email address', maxLength: 50 },
  state: { label: 'State', maxLength: 200 },
  city: { label: 'City', maxLength: 200 },
  postal_code: { label: 'ZIP or postal code' },
  country_alpha2: { label: 'Country code' },
  total_actual_weight: { label: 'Package weight' },
  length: { label: 'Package length' },
  width: { label: 'Package width' },
  height: { label: 'Package height' },
  items: { label: 'Package items' },
};

const FIELD_ERROR_OWNERS = new Set([
  'RETURN_LOCATION',
  'CUSTOMER_ADDRESS',
  'PACKAGE',
]);
const FIELD_ERROR_CODES = new Set([
  'REQUIRED',
  'INVALID',
  'INVALID_FORMAT',
  'TOO_LONG',
  'MAX_LENGTH',
]);

const safeFieldErrorMessage = (
  owner: string,
  field: string,
  code: string,
): string => {
  const presentation = FIELD_PRESENTATIONS[field] ?? {
    label: 'Return label information',
  };
  const ownerLabel =
    owner === 'RETURN_LOCATION'
      ? `Return location ${presentation.label.toLowerCase()}`
      : owner === 'CUSTOMER_ADDRESS'
      ? `Customer shipping ${presentation.label.toLowerCase()}`
      : presentation.label;

  switch (code) {
    case 'REQUIRED':
      return `${ownerLabel} is required.`;
    case 'TOO_LONG':
    case 'MAX_LENGTH':
      return presentation.maxLength
        ? `${ownerLabel} must be ${presentation.maxLength} characters or fewer.`
        : `${ownerLabel} is too long.`;
    case 'INVALID':
    case 'INVALID_FORMAT':
      return `${ownerLabel} is invalid.`;
    default:
      return `${ownerLabel} needs attention.`;
  }
};

const normalizedFieldErrors = (value: unknown): ReturnLabelFieldError[] => {
  if (!Array.isArray(value)) return [];

  return value.flatMap((entry) => {
    if (!entry || typeof entry !== 'object') return [];
    const record = entry as Record<string, unknown>;
    const owner = upperValue(record.owner);
    const field = stringValue(record.field)?.toLowerCase() ?? null;
    const code = upperValue(record.code);
    if (
      !owner ||
      !FIELD_ERROR_OWNERS.has(owner) ||
      !field ||
      !FIELD_PRESENTATIONS[field] ||
      !code ||
      !FIELD_ERROR_CODES.has(code)
    ) {
      return [];
    }

    return [
      {
        owner,
        field,
        code,
        message: safeFieldErrorMessage(owner, field, code),
      },
    ];
  });
};

const errorContract = (
  error?: ApolloErrorLike | null,
): ReturnLabelErrorContract => {
  const extensions = error?.graphQLErrors?.find(
    (graphQLError) => graphQLError?.extensions,
  )?.extensions;
  if (!extensions) return {};

  const nested = extensions.returnLabel;
  const nestedContract =
    nested && typeof nested === 'object'
      ? (nested as ReturnLabelErrorContract)
      : {};

  return {
    ...nestedContract,
    ...extensions,
  };
};

const CODE_KINDS: Record<string, ReturnLabelErrorCategory> = {
  RETURN_LABEL_RETURN_LOCATION_INVALID: 'ADDRESS',
  RETURN_LABEL_CUSTOMER_ADDRESS_INVALID: 'ADDRESS',
  RETURN_LABEL_EASYSHIP_REAUTH_REQUIRED: 'CONNECTION',
  RETURN_LABEL_EASYSHIP_ACCOUNT_ACTION_REQUIRED: 'ACCOUNT_ACTION',
  RETURN_LABEL_PROVIDER_RATE_LIMITED: 'TRANSIENT',
  RETURN_LABEL_PROVIDER_TEMPORARY: 'TRANSIENT',
  RETURN_LABEL_PROVIDER_REJECTED: 'METHOD',
  RETURN_LABEL_INTERNAL_DEPENDENCY: 'INTERNAL',
  RETURN_LABEL_OUTCOME_UNKNOWN: 'UNKNOWN',
  RETURN_LABEL_PARTIAL_CREATED: 'UNKNOWN',
  RETURN_LABEL_GENERATION_IN_PROGRESS: 'UNKNOWN',
  RETURN_LABEL_NOT_READY: 'UNKNOWN',
  RETURN_SHIPPING_PHONE_REQUIRED: 'ADDRESS',
  RETURN_SHIPPING_PHONE_INVALID: 'ADDRESS',
};

const ACTION_KINDS: Record<string, ReturnLabelErrorCategory> = {
  UPDATE_RETURN_LOCATION: 'ADDRESS',
  UPDATE_CUSTOMER_ADDRESS: 'ADDRESS',
  RECONNECT_EASYSHIP: 'CONNECTION',
  REVIEW_EASYSHIP_ACCOUNT: 'ACCOUNT_ACTION',
  RETRY_LATER: 'TRANSIENT',
  REVIEW_LABEL_REQUEST: 'METHOD',
  CONTACT_SHIPAID: 'INTERNAL',
  WAIT_FOR_RECONCILIATION: 'UNKNOWN',
  REFRESH_RETURN: 'UNKNOWN',
};

const PARCEL_FIELDS = new Set([
  'total_actual_weight',
  'length',
  'width',
  'height',
  'items',
]);

const kindFromContract = (
  code: string | null,
  action: string | null,
  fieldErrors: ReturnLabelFieldError[],
  hasNetworkError: boolean,
): ReturnLabelErrorCategory => {
  if (fieldErrors.some((error) => PARCEL_FIELDS.has(error.field))) {
    return 'PARCEL';
  }
  if (
    fieldErrors.some((error) =>
      ['RETURN_LOCATION', 'CUSTOMER_ADDRESS'].includes(error.owner),
    )
  ) {
    return 'ADDRESS';
  }
  if (code && CODE_KINDS[code]) return CODE_KINDS[code];
  if (action && ACTION_KINDS[action]) return ACTION_KINDS[action];
  if (hasNetworkError) return 'TRANSIENT';
  return 'UNKNOWN';
};

const isNarrowLegacyConnectionError = (message?: string): boolean => {
  const normalized = message?.trim() ?? '';
  return [
    /^Easyship merchant account is not connected for return labels\.?$/i,
    /^Easyship merchant account connection is required to create merchant carrier account return labels\.?$/i,
    /^Easyship merchant account token is expired\. Reconnect Easyship to create return labels\.?$/i,
  ].some((pattern) => pattern.test(normalized));
};

const presentationCopy = (
  kind: ReturnLabelErrorCategory,
  mayHaveCreatedLabel: boolean,
  code: string | null,
  action: string | null,
  fieldErrors: ReturnLabelFieldError[],
): Pick<
  ReturnLabelErrorPresentation,
  'message' | 'actionLabel' | 'actionTarget'
> => {
  if (code === 'RETURN_LABEL_GENERATION_IN_PROGRESS') {
    return {
      message:
        'Label generation may still be in progress, and a label may already exist. Do not try again. Refresh this return; if the label does not appear, contact SHIPAID support.',
    };
  }

  if (code === 'RETURN_LABEL_NOT_READY') {
    return {
      message:
        'This return is not ready for label creation yet. Refresh the return before taking another action.',
      actionLabel: 'Refresh return',
    };
  }

  if (mayHaveCreatedLabel) {
    return {
      message:
        'A label may already exist. Do not try again. Refresh this return; if the label does not appear, contact SHIPAID support.',
    };
  }

  switch (kind) {
    case 'CONNECTION':
      return {
        message:
          'Easyship authorization is required before this carrier-account label can be created.',
        actionLabel: 'Contact SHIPAID support',
        actionTarget: SUPPORT_EMAIL_PATH,
      };
    case 'ADDRESS': {
      const addressOwners = new Set(fieldErrors.map(({ owner }) => owner));
      if (
        addressOwners.has('RETURN_LOCATION') &&
        addressOwners.has('CUSTOMER_ADDRESS')
      ) {
        return {
          message:
            'Update the customer shipping address in Shopify and review the return shipping address in SHIPAID before trying again.',
          actionLabel: 'Review return address',
          actionTarget: RETURN_SETTINGS_PATH,
        };
      }
      if (
        code === 'RETURN_LABEL_CUSTOMER_ADDRESS_INVALID' ||
        action === 'UPDATE_CUSTOMER_ADDRESS' ||
        addressOwners.has('CUSTOMER_ADDRESS')
      ) {
        return {
          message:
            'Update the customer shipping address in Shopify, then refresh this return before trying again. If you cannot update it, contact SHIPAID support.',
          actionLabel: 'Contact SHIPAID support',
          actionTarget: SUPPORT_EMAIL_PATH,
        };
      }
      return {
        message:
          'Review the return shipping address in SHIPAID and correct the listed issues before trying again.',
        actionLabel: 'Review return address',
        actionTarget: RETURN_SETTINGS_PATH,
      };
    }
    case 'PARCEL':
      return {
        message:
          'Review the return package weight and dimensions before trying again.',
        actionLabel: 'Review package settings',
        actionTarget: RETURN_SETTINGS_PATH,
      };
    case 'METHOD':
      return {
        message:
          'Review the package details and shipping method before trying this label request again.',
        actionLabel: 'Review label request',
        actionTarget: RETURN_SETTINGS_PATH,
      };
    case 'ACCOUNT_ACTION':
      return {
        message:
          'The carrier account needs attention before this label can be created.',
        actionLabel: 'Review Easyship account',
        actionTarget: 'https://app.easyship.com/',
      };
    case 'TRANSIENT':
      return {
        message:
          'The label provider is temporarily unavailable. Try again after the suggested wait.',
      };
    case 'INTERNAL':
      return {
        message:
          'SHIPAID could not finish creating this label. Contact support before trying again.',
        actionLabel: 'Contact SHIPAID support',
        actionTarget: SUPPORT_EMAIL_PATH,
      };
    case 'UNKNOWN':
    default:
      return {
        message:
          'SHIPAID could not confirm the label outcome. Refresh this return before trying again.',
        actionLabel: 'Contact SHIPAID support',
        actionTarget: SUPPORT_EMAIL_PATH,
      };
  }
};

export const getReturnLabelErrorPresentation = (
  error?: ApolloErrorLike | null,
  options: { allowLegacyAuthFallback?: boolean } = {},
): ReturnLabelErrorPresentation => {
  const contract = errorContract(error);
  const code = upperValue(contract.code);
  const category = upperValue(contract.category);
  const action = upperValue(contract.action);
  const retryDisposition = upperValue(contract.retryDisposition);
  const fieldErrors = normalizedFieldErrors(contract.fieldErrors);
  const explicitReconnect = contract.reconnectRequired === true;
  const legacyReconnect = Boolean(
    options.allowLegacyAuthFallback &&
      isNarrowLegacyConnectionError(error?.message),
  );
  const reconnectRequired = explicitReconnect || legacyReconnect;
  const outcome =
    upperValue(contract.outcome) ??
    (reconnectRequired ? 'NOT_CREATED' : 'UNKNOWN');
  const contractKind = kindFromContract(
    code,
    action,
    fieldErrors,
    Boolean(error?.networkError),
  );
  const kind = reconnectRequired
    ? 'CONNECTION'
    : contractKind === 'CONNECTION'
    ? 'INTERNAL'
    : contractKind;
  const mayHaveCreatedLabel = [
    'UNKNOWN',
    'SHIPMENT_CREATED',
    'LABEL_CREATED',
  ].includes(outcome);
  const shouldPoll = mayHaveCreatedLabel || retryDisposition === 'RECONCILE';
  const canRetry =
    !reconnectRequired &&
    !shouldPoll &&
    outcome === 'NOT_CREATED' &&
    ['CORRECT_AND_RETRY', 'RETRY_LATER'].includes(retryDisposition ?? '');
  const retryAfterSeconds = finiteNonNegativeNumber(contract.retryAfterSeconds);
  const copy = presentationCopy(
    kind,
    mayHaveCreatedLabel,
    code,
    action,
    fieldErrors,
  );
  const retryLaterPrefix =
    kind === 'TRANSIENT'
      ? 'The label provider is temporarily unavailable.'
      : 'SHIPAID could not finish creating this label.';
  const message =
    retryDisposition === 'RETRY_LATER' &&
    outcome === 'NOT_CREATED' &&
    !mayHaveCreatedLabel
      ? retryAfterSeconds === 0
        ? `${retryLaterPrefix} You can try again now.`
        : retryAfterSeconds && retryAfterSeconds > 0
        ? `${retryLaterPrefix} Try again in ${retryAfterSeconds} seconds.`
        : `${retryLaterPrefix} Try again in a few minutes.`
      : copy.message;

  return {
    kind,
    code,
    category,
    action,
    outcome,
    retryDisposition,
    retryAfterSeconds,
    reconnectRequired,
    fieldErrors,
    supportReference: stringValue(contract.supportReference),
    canRetry,
    shouldPoll,
    mayHaveCreatedLabel,
    ...copy,
    message,
  };
};

const PERSISTED_LABEL_FAILURE_STATUSES = new Set([
  'FAILED',
  'FAILED_REVIEW_REQUIRED',
  'IN_PROGRESS',
  'PENDING',
]);

export const getPersistedReturnLabelErrorPresentation = (
  meta: unknown,
): ReturnLabelErrorPresentation | null => {
  if (!meta || typeof meta !== 'object' || Array.isArray(meta)) return null;
  const labelGeneration = (meta as Record<string, unknown>).labelGeneration;
  if (
    !labelGeneration ||
    typeof labelGeneration !== 'object' ||
    Array.isArray(labelGeneration)
  ) {
    return null;
  }

  const persisted = labelGeneration as Record<string, unknown>;
  const status = upperValue(persisted.status);
  if (!status || !PERSISTED_LABEL_FAILURE_STATUSES.has(status)) return null;

  const requiresReconciliation =
    status === 'FAILED_REVIEW_REQUIRED' ||
    status === 'IN_PROGRESS' ||
    status === 'PENDING' ||
    persisted.lockRetained === true;
  const persistedRetryDisposition = upperValue(persisted.retryDisposition);
  const persistedRetryAfterSeconds = finiteNonNegativeNumber(
    persisted.retryAfterSeconds,
  );
  const failedAt = stringValue(persisted.failedAt);
  const failedAtMs = failedAt ? Date.parse(failedAt) : Number.NaN;
  const remainingRetryAfterSeconds =
    status === 'FAILED' &&
    persistedRetryDisposition === 'RETRY_LATER' &&
    persistedRetryAfterSeconds !== null &&
    Number.isFinite(failedAtMs)
      ? Math.max(
          0,
          Math.ceil(
            (failedAtMs + persistedRetryAfterSeconds * 1000 - Date.now()) /
              1000,
          ),
        )
      : persisted.retryAfterSeconds;

  const presentation = getReturnLabelErrorPresentation({
    graphQLErrors: [
      {
        extensions: {
          code: persisted.errorCode,
          category: persisted.errorCategory,
          action:
            persisted.errorAction ??
            (requiresReconciliation ? 'WAIT_FOR_RECONCILIATION' : undefined),
          outcome:
            persisted.providerOutcome ??
            (requiresReconciliation ? 'UNKNOWN' : 'NOT_CREATED'),
          retryDisposition:
            persistedRetryDisposition ??
            (requiresReconciliation ? 'RECONCILE' : undefined),
          retryAfterSeconds: remainingRetryAfterSeconds,
          reconnectRequired: persisted.reconnectRequired === true,
          fieldErrors: persisted.fieldErrors,
          supportReference: persisted.supportReference,
        },
      },
    ],
  });

  return {
    ...presentation,
    persistedStatus: status,
    lockRetained:
      typeof persisted.lockRetained === 'boolean'
        ? persisted.lockRetained
        : undefined,
  };
};
